"use client";

import type { MouseEvent } from "react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils/cn";
import { tourPageAnchorNavThemeStyles as themeStyles } from "./themeStyles";
import type { TourPageAnchorLink, TourPageThemeKey } from "./types";

interface TourPageAnchorNavProps {
  theme: TourPageThemeKey;
  label: string;
  links: readonly TourPageAnchorLink[];
}

function getVisibleStickyHeight(selector: string) {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) return 0;

  const styles = window.getComputedStyle(element);
  if (styles.display === "none" || styles.visibility === "hidden") return 0;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return 0;
  if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;

  const canOverlayContent =
    styles.position === "fixed" || styles.position === "sticky";

  return canOverlayContent ? rect.height : 0;
}

function getStickyChromeOffset() {
  const headerHeight = getVisibleStickyHeight("header");
  const anchorNavHeight = getVisibleStickyHeight("[data-tour-page-anchor-nav]");

  return Math.ceil(headerHeight + anchorNavHeight + 16);
}

export default function TourPageAnchorNav({
  theme,
  label,
  links,
}: TourPageAnchorNavProps) {
  const [activeHref, setActiveHref] = useState<string>(
    links[0]?.href ?? "#stops",
  );
  const styles = themeStyles[theme];

  const handleAnchorClick = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    const target = document.getElementById(href.slice(1));
    if (!target) return;

    event.preventDefault();
    const documentElement = document.documentElement;
    const previousScrollBehavior = documentElement.style.scrollBehavior;
    const scrollOffset = getStickyChromeOffset();
    const targetTop =
      window.scrollY + target.getBoundingClientRect().top - scrollOffset;

    documentElement.style.scrollBehavior = "auto";
    window.history.pushState(null, "", href);
    window.scrollTo({ top: targetTop, behavior: "auto" });
    setActiveHref(href);

    window.requestAnimationFrame(() => {
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      documentElement.style.scrollBehavior = previousScrollBehavior;
    });
  };

  useEffect(() => {
    let animationFrame = 0;

    const updateActiveSection = () => {
      const nav = document.querySelector<HTMLElement>(
        "[data-tour-page-anchor-nav]",
      );
      const activationLine = Math.max(
        (nav?.getBoundingClientRect().bottom ?? getStickyChromeOffset()) + 16,
        window.innerHeight * 0.36,
      );
      const documentElement = document.documentElement;
      const isAtPageBottom =
        window.scrollY + window.innerHeight >= documentElement.scrollHeight - 2;

      if (isAtPageBottom && links.length > 0) {
        setActiveHref(links[links.length - 1].href);
        return;
      }

      let currentHref: string = links[0]?.href ?? "#stops";
      let matchedCurrentSection = false;

      for (const link of links) {
        for (const sectionId of link.sectionIds) {
          const section = document.getElementById(sectionId);
          if (!section) continue;

          const rect = section.getBoundingClientRect();

          if (rect.top <= activationLine && rect.bottom > activationLine) {
            currentHref = link.href;
            matchedCurrentSection = true;
            break;
          }

          if (rect.top <= activationLine) {
            currentHref = link.href;
          }
        }

        if (matchedCurrentSection) break;
      }

      setActiveHref(currentHref);
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    const observer = new IntersectionObserver(scheduleUpdate, {
      rootMargin: "-20% 0px -55% 0px",
      threshold: [0, 0.01, 0.25, 0.5, 1],
    });

    links.forEach((link) => {
      link.sectionIds.forEach((sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) observer.observe(section);
      });
    });

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("hashchange", scheduleUpdate);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("hashchange", scheduleUpdate);
    };
  }, [links]);

  return (
    <nav
      aria-label={label}
      data-tour-page-anchor-nav
      className={cn(
        "sticky top-20 z-40 border-b shadow-[0_8px_20px_rgba(33,26,23,0.08)] xl:top-[96px] xl:border-y",
        styles.navShell,
      )}
    >
      <ul className="m-0 flex list-none overflow-x-auto scroll-smooth p-0 [scrollbar-width:thin] motion-reduce:scroll-auto xl:mx-auto xl:max-w-6xl xl:justify-center">
        {links.map((link) => {
          const isActive = activeHref === link.href;

          return (
            <li
              key={link.href}
              className="min-w-[60px] flex-1 snap-start xl:min-w-[132px] xl:max-w-[180px]"
            >
              <a
                href={link.href}
                aria-current={isActive ? "location" : undefined}
                onClick={(event) => handleAnchorClick(event, link.href)}
                className={cn(
                  "flex min-h-12 min-w-0 items-center justify-center border-b-2 px-1 text-center text-[12px] font-bold leading-tight transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] sm:text-[13px] xl:min-h-11 xl:px-3",
                  styles.navFocus,
                  isActive ? styles.navActive : styles.navInactive,
                )}
              >
                {link.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
